import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Button,
  Divider,
} from '@mui/material';
import {
  RestartAlt,
  Visibility,
  VisibilityOff,
  DragIndicator,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import { DashboardLayoutConfig } from '../services/dashboard-layout-service';

/** Metadata for each available dashboard widget. */
export interface WidgetDefinition {
  id: string;
  label: string;
  /** MUI Grid xs size when displayed. */
  gridXs: number;
  /** MUI Grid md size when displayed. */
  gridMd: number;
}

/**
 * Widgets below the fixed summary sections.
 *
 * The summary (needs attention, net worth, bills, spending, analysis, cash
 * flow, recent activity -- issue #27) is always shown and comes from one
 * call. The old Net Worth Card, Quick Stats and Recent Activity widgets
 * duplicated it -- Quick Stats with a "Last Updated" that read Dec 31 in
 * October -- so they are gone from the catalog; a saved layout naming them
 * simply drops them (the page filters unknown ids).
 */
export const WIDGET_CATALOG: WidgetDefinition[] = [
  { id: 'net-worth-chart', label: 'Net Worth Chart', gridXs: 12, gridMd: 12 },
  { id: 'accounts-overview', label: 'Accounts Overview', gridXs: 12, gridMd: 12 },
  { id: 'portfolio-summary', label: 'Portfolio Summary', gridXs: 12, gridMd: 6 },
];

/** All widget IDs in default order. */
export const DEFAULT_ORDER = WIDGET_CATALOG.map((w) => w.id);

/** Widgets visible by default: the trend chart and the accounts. */
const DEFAULT_VISIBLE = ['net-worth-chart', 'accounts-overview'];

/**
 * Build the default layout config used when no saved layout exists.
 *
 * Returns:
 *   DashboardLayoutConfig: Config with the default widgets visible in catalog order.
 */
export const buildDefaultConfig = (): DashboardLayoutConfig => ({
  visibleWidgets: [...DEFAULT_VISIBLE],
  order: [...DEFAULT_ORDER],
});

interface DashboardEditPanelProps {
  layoutConfig: DashboardLayoutConfig;
  widgetMap: Map<string, WidgetDefinition>;
  onToggleWidget: (widgetId: string) => void;
  onMoveWidget: (widgetId: string, direction: 'up' | 'down') => void;
  onReset: () => void;
}

/**
 * Panel displayed while the dashboard is in edit mode.
 * Shows toggleable widget chips with reorder arrows.
 *
 * Args:
 *   layoutConfig: Current layout configuration.
 *   widgetMap: Lookup map from widget ID to definition.
 *   onToggleWidget: Called when a widget visibility chip is clicked.
 *   onMoveWidget: Called when an up/down arrow is clicked.
 *   onReset: Called when the "Reset to Default" button is clicked.
 */
export const DashboardEditPanel: React.FC<DashboardEditPanelProps> = ({
  layoutConfig,
  widgetMap,
  onToggleWidget,
  onMoveWidget,
  onReset,
}) => {
  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        mb: 3,
        border: '2px dashed',
        borderColor: 'primary.main',
        bgcolor: 'action.hover',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Customize Widgets
        </Typography>
        <Button
          startIcon={<RestartAlt />}
          onClick={onReset}
          variant="outlined"
          color="warning"
          size="small"
        >
          Reset to Default
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Toggle widgets on or off. Use the arrows to reorder them.
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {layoutConfig.order.map((widgetId, index) => {
          const def = widgetMap.get(widgetId);
          if (!def) return null;
          const isVisible = layoutConfig.visibleWidgets.includes(widgetId);
          return (
            <Box
              key={widgetId}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                borderRadius: 1,
                bgcolor: isVisible ? 'background.paper' : 'action.disabledBackground',
                border: '1px solid',
                borderColor: isVisible ? 'divider' : 'transparent',
              }}
            >
              <DragIndicator color="disabled" fontSize="small" />
              <Chip
                icon={
                  isVisible ? (
                    <Visibility fontSize="small" />
                  ) : (
                    <VisibilityOff fontSize="small" />
                  )
                }
                label={def.label}
                onClick={() => onToggleWidget(widgetId)}
                color={isVisible ? 'primary' : 'default'}
                variant={isVisible ? 'filled' : 'outlined'}
                sx={{ minWidth: 180, justifyContent: 'flex-start' }}
              />
              <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                <IconButton
                  size="small"
                  disabled={index === 0}
                  onClick={() => onMoveWidget(widgetId, 'up')}
                  aria-label={`Move ${def.label} up`}
                >
                  <ArrowUpward fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={index === layoutConfig.order.length - 1}
                  onClick={() => onMoveWidget(widgetId, 'down')}
                  aria-label={`Move ${def.label} down`}
                >
                  <ArrowDownward fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};
